// bulkSpareLotApiSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../../apiSlice";

/* ------------------ Adapter ------------------ */
const bulkSpareLotAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const initialState = bulkSpareLotAdapter.getInitialState();

/* ------------------ API Slice ------------------ */
export const bulkSpareLotSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* -------- LOT LIST -------- */
    getBulkSpareLotList: builder.query({
      query: ({ request_id, seller_id, status } = {}) => {
        let queryString = `/bulk_deals/spares/${request_id}/items`;
        const params = new URLSearchParams();

        if (seller_id) params.append("seller_id", seller_id);
        if (status) params.append("status", status);

        if (params.toString()) queryString += `?${params.toString()}`;
        return queryString;
      },

      transformResponse: (response) =>
        bulkSpareLotAdapter.setAll(initialState, response?.data ?? []),

      providesTags: (result) =>
        result
          ? [
              { type: "BulkSpareLot", id: "LIST" },
              ...result.ids.map((id) => ({ type: "BulkSpareLot", id })),
            ]
          : [{ type: "BulkSpareLot", id: "LIST" }],
    }),

    /* -------- LOT ITEM BY ID -------- */
    getBulkSpareLotById: builder.query({
      query: ({ request_id, itemId }) =>
        `/bulk_deals/spares/${request_id}/items/${itemId}`,

      providesTags: (result, error, { itemId }) => [
        { type: "BulkSpareLot", id: itemId },
      ],
    }),

    /* -------- UPDATE LOT ITEM (PATCH) -------- */
    updateBulkSpareLotItem: builder.mutation({
      query: ({ request_id, itemId, data }) => ({
        url: `/bulk_deals/spares/${request_id}/items/${itemId}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (result, error, { itemId }) => [
        { type: "BulkSpareLot", id: itemId },
        { type: "BulkSpareLot", id: "LIST" },
      ],
    }),
  }),
});

/* ------------------ Hooks ------------------ */
export const {
  useGetBulkSpareLotListQuery,
  useGetBulkSpareLotByIdQuery,
  useUpdateBulkSpareLotItemMutation,
} = bulkSpareLotSlice;

/* ------------------ Selectors ------------------ */

/* FILTER STATE */
const bulkSparesLotFilter = (state) => state.bulkSparesLotFilter;

/* QUERY RESULT SELECTOR */
const selectBulkSpareLotQueryResult = createSelector(
  [bulkSparesLotFilter, (state) => state],
  (filters, state) => {
    return bulkSpareLotSlice.endpoints.getBulkSpareLotList.select({
      request_id: filters.request_id,
      seller_id: filters.seller_id,
      status: filters.status,
    })(state);
  },
);

/* DATA SELECTOR */
const selectBulkSpareLotData = createSelector(
  [selectBulkSpareLotQueryResult],
  (queryResult) => queryResult?.data ?? initialState,
);

/* ENTITY SELECTORS */
export const {
  selectAll: selectBulkSpareLotItems,
  selectById: selectBulkSpareLotItemById,
  selectIds: selectBulkSpareLotItemIds,
} = bulkSpareLotAdapter.getSelectors((state) => selectBulkSpareLotData(state));
